/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as d3 from "d3";
import { Feature } from "geojson";

import * as Color from "~/Color";
import { CountProperties } from "~/Dashboard/MapBox";
import * as Driver from "~/Driver";

export type ChartData = {
  readonly status: Driver.Status.Status;
  readonly count: number;
};

const generateChartContainer = (
  width: number,
  height: number,
  elementClassName: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any =>
  d3
    .select(`.${elementClassName}`)
    // .append("svg")
    .attr("width", width)
    .attr("height", height)
    .attr("style", `background-color: ${Color.white()}`);

const generateMainTransform = (
  element: d3.Selection<SVGGElement, ChartData, HTMLElement, unknown>,
  left: number,
  top: number
): d3.Selection<SVGGElement, ChartData, HTMLElement, unknown> =>
  element.append("g").attr("transform", `translate(${left},${top})`);

const generateYScale = (
  height: number
): d3.ScaleLinear<number, number, never> =>
  d3.scaleLinear().rangeRound([height, 0]).nice();

const generateXScale = (width: number): d3.ScaleBand<string> =>
  d3.scaleBand().rangeRound([0, width]).padding(0.2);

const generateChartGroup = (
  mainTransform: d3.Selection<SVGGElement, ChartData, HTMLElement, unknown>,
  width: number,
  height: number
) => mainTransform.attr("width", width).attr("height", height);

const drawXAxis = (
  mainTransform: d3.Selection<SVGGElement, ChartData, HTMLElement, unknown>,
  xScale: d3.ScaleBand<string>,
  height: number
) =>
  mainTransform
    .append("g")
    .attr("class", "axis axis-x")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(xScale));

const drawYAxis = (
  mainTransform: d3.Selection<SVGGElement, ChartData, HTMLElement, unknown>,
  clusterPointCount: number,
  yScale: d3.ScaleLinear<number, number, never>
) => {
  if (clusterPointCount < 10) {
    mainTransform
      .append("g")
      .attr("class", "axis axis-y")
      .call(d3.axisLeft(yScale).ticks(clusterPointCount + 1));
  } else {
    mainTransform
      .append("g")
      .attr("class", "axis axis-y")
      .call(d3.axisLeft(yScale));
  }
};

const drawBars = (
  yScale: d3.ScaleLinear<number, number, never>,
  xScale: d3.ScaleBand<string>,
  mainTransform: d3.Selection<SVGGElement, ChartData, HTMLElement, unknown>,
  height: number
) =>
  // @ts-ignore
  mainTransform
    .append("rect")
    .attr("class", "bar")
    .attr("width", xScale.bandwidth())
    .attr("height", (d) => height - yScale(d.count))
    .attr("x", (d) => xScale(d.status) as number)
    .attr("y", (d) => yScale(d.count))
    .attr("fill", (d) => Driver.Status.toColor(d.status));

const generateBarChart = (
  xScale: d3.ScaleBand<string>,
  yScale: d3.ScaleLinear<number, number, never>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  element: any,
  data: readonly ChartData[],
  clusterPointCount: number,
  height: number
) => {
  const selection = element.selectAll(".bar").data(data).enter();
  drawXAxis(element, xScale, height);
  drawYAxis(element, clusterPointCount, yScale);
  drawBars(yScale, xScale, selection, height);

  element
    .selectAll("text.bar")
    .data(data)
    .enter()
    .append("text")
    .attr("class", "bar")
    .attr("text-anchor", "middle")
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    .attr("x", (d: ChartData) => xScale(d.status) + xScale.bandwidth() / 2)
    .attr("y", (d: ChartData) => yScale(d.count) - 5)
    .text((d: ChartData) => (d.count > 0 ? d.count : ""));
};

const transformData = (features: readonly Feature[]): readonly ChartData[] => {
  const reducedData = features.reduce<CountProperties>(
    (accumulator, value) => {
      const properties: CountProperties = value.properties as CountProperties;
      return {
        driveCount: accumulator.driveCount + properties.driveCount,
        onCount: accumulator.onCount + properties.onCount,
        offCount: accumulator.offCount + properties.offCount,
        sbCount: accumulator.sbCount + properties.sbCount,
        ymCount: accumulator.ymCount + properties.ymCount,
        puCount: accumulator.puCount + properties.puCount,
      };
    },
    {
      driveCount: 0,
      onCount: 0,
      offCount: 0,
      sbCount: 0,
      ymCount: 0,
      puCount: 0,
    }
  );

  return [
    { status: "D", count: reducedData.driveCount },
    { status: "ON", count: reducedData.onCount },
    { status: "OFF", count: reducedData.offCount },
    { status: "SB", count: reducedData.sbCount },
    { status: "YM", count: reducedData.ymCount },
    { status: "PU", count: reducedData.puCount },
  ];
};
export const generateChart = (
  features: readonly Feature[],
  clusterPointCount: number,
  elementClassName: string
) => {
  const containerHeight = 245;
  const containerWidth = 230;
  const leftMargin = 23;
  const rightMargin = 20;
  const topMargin = 40;
  const bottomMargin = 20;
  const chartHeight: number = containerHeight - topMargin - bottomMargin;
  const chartWidth: number = containerWidth - leftMargin - rightMargin;

  const clusterPoints: readonly ChartData[] = transformData(features);
  const svg = generateChartContainer(chartWidth, chartHeight, elementClassName);
  const mainTransform = generateMainTransform(svg, leftMargin, topMargin);

  // Add a title to the chart
  mainTransform
    .append("g")
    .append("text")
    .attr("x", chartHeight / 2)
    .attr("y", 0 - topMargin / 2)
    .attr("text-anchor", "middle")
    .style("font-size", "16px")
    .text("Cluster Statistics");

  const yScale = generateYScale(chartHeight);
  const xScale = generateXScale(chartWidth);
  xScale.domain(clusterPoints.map((d) => d.status));
  yScale.domain([0, clusterPointCount]);

  const chartGroup = generateChartGroup(mainTransform, chartWidth, chartHeight);
  generateBarChart(
    xScale,
    yScale,
    chartGroup,
    clusterPoints,
    clusterPointCount,
    chartHeight
  );
};
